import { useCallback, useState } from "react";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { selectAssistantMeta, selectAssistantValue } from "../redux/selectors";
import { TEntityInfo } from "../../../common/types/utility";
import { AssistantValue } from "../../../services/api/instances/assistantsApi/types";
import { getUpdatedValue } from "../helpers/getUpdatedValue";
import { TValueForService } from "../../../services/api/instances/storageApi/types/service_types";
import { ASSISTANT_PLUGIN, SKILLS_PLUGIN } from "../Constants";
import { rebuildSecurityBlocks } from "../helpers/rebuildSecurityBlocks";
import { TSkill } from "../../../services/api/instances/skillsApi/types";
import { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";

export const useAssistantTool = (
  saveAssistant: (
    value: TValueForService<"assistants">,
    withDebounce?: boolean,
  ) => void,
) => {
  const meta = useAppSelector(selectAssistantMeta);
  const assistantValue = useAppSelector(selectAssistantValue);
  const [openInstructionBlock, setOpenInstructionBlock] =
    useState<TEntityInfo | null>(null);

  const handleCloseInstructionBlock = () => setOpenInstructionBlock(null);

  const handleOpenInstructionBlock = (id: string, value: string) =>
    setOpenInstructionBlock({ id, value });

  const onSelectAssistant = useCallback(
    (assistant: NormalizedStorageObject<AssistantValue> | null, id: string) => {
      if (!assistantValue || !meta?.tools || !meta.tools?.assistants) return;
      const updatedTools = meta.tools.assistants.map((tool) => {
        if (tool.id === id) {
          const newRef = assistant?.ref ?? "";
          const newWhen = tool.when ?? "";
          return {
            ...tool,
            ref: newRef,
            when: newWhen,
          };
        }
        return tool;
      });
      const newAssistantTool = assistantValue.assistant_object?.tools.some(
        (tool) => tool.function?.name === ASSISTANT_PLUGIN.function.name,
      )
        ? assistantValue.assistant_object?.tools
        : [...assistantValue?.assistant_object?.tools, ASSISTANT_PLUGIN];
      const finalInstruction = rebuildSecurityBlocks(
        assistantValue?.assistant_object?.instructions,
        updatedTools,
        meta?.tools?.skills ?? null,
      );
      const newValue = getUpdatedValue(
        assistantValue,
        {
          instructions: finalInstruction,
          tools: newAssistantTool,
        },
        { tools: { ...meta.tools, assistants: updatedTools } },
      );

      saveAssistant(newValue);
    },
    [
      assistantValue?.assistant_object?.instructions,
      meta?.tools?.assistants,
      saveAssistant,
    ],
  );

  const onSelectSkill = useCallback(
    (skill: TSkill | null, id: string) => {
      if (!assistantValue || !meta?.tools || !meta.tools?.skills) return;
      const updatedTools = meta.tools.skills.map((tool) => {
        if (tool.id === id) {
          if (!skill) {
            return {
              ...tool,
              name: "",
              friendly_name: "",
              description: "",
              path: "",
              tags: [],
            };
          }
          return {
            id: id,
            name: skill.name,
            friendly_name: skill.friendly_name,
            description: skill.description,
            path: skill.path,
            tags: skill.tags,
          };
        }
        return tool;
      });
      const finalInstruction = rebuildSecurityBlocks(
        assistantValue?.assistant_object?.instructions,
        meta?.tools?.assistants ?? null,
        updatedTools,
      );
      const newAssistantTool = assistantValue.assistant_object?.tools.some(
        (tool) => tool.function?.name === SKILLS_PLUGIN.function.name,
      )
        ? assistantValue.assistant_object?.tools
        : [...assistantValue?.assistant_object?.tools, SKILLS_PLUGIN];
      const newValue = getUpdatedValue(
        assistantValue,
        {
          instructions: finalInstruction,
          tools: newAssistantTool,
        },
        { tools: { ...meta.tools, skills: updatedTools } },
      );

      saveAssistant(newValue);
    },
    [assistantValue, meta?.tools?.skills, saveAssistant],
  );

  const handleChangeInstruction = useCallback(
    (instruction: string, id: string) => {
      if (!assistantValue || !meta?.tools || !meta.tools?.assistants) return;
      const updatedTools = meta.tools.assistants.map((tool) => {
        if (tool.id === id) {
          return {
            ...tool,
            when: instruction,
          };
        }
        return tool;
      });
      const finalInstruction = rebuildSecurityBlocks(
        assistantValue?.assistant_object?.instructions,
        updatedTools,
        meta?.tools?.skills ?? null,
      );

      const newValue = getUpdatedValue(
        assistantValue,
        {
          instructions: finalInstruction,
        },
        { tools: { ...meta.tools, assistants: updatedTools } },
      );

      saveAssistant(newValue);
    },
    [
      assistantValue?.assistant_object?.instructions,
      meta?.tools,
      saveAssistant,
    ],
  );

  const handleRemoveAssistant = useCallback(
    (id: string) => {
      if (!assistantValue || !meta?.tools || !meta.tools?.assistants) return;
      const updatedTools = meta.tools.assistants.filter(
        (tool) => tool.id !== id,
      );

      const newAssistantTool =
        updatedTools?.length === 0
          ? assistantValue.assistant_object.tools.filter(
              (t) => t.function?.name !== ASSISTANT_PLUGIN.function.name,
            )
          : assistantValue.assistant_object.tools;
      const finalInstruction = rebuildSecurityBlocks(
        assistantValue?.assistant_object?.instructions,
        updatedTools,
        meta?.tools?.skills ?? null,
      );
      const newValue = getUpdatedValue(
        assistantValue,
        {
          instructions: finalInstruction,
          tools: newAssistantTool,
        },
        { tools: { ...meta.tools, assistants: updatedTools } },
      );

      setOpenInstructionBlock(null);
      saveAssistant(newValue);
    },
    [meta?.tools],
  );

  const handleDeleteSkill = useCallback(
    (id: string) => {
      if (!assistantValue || !meta?.tools || !meta.tools?.skills) return;
      const updatedTools = meta.tools.skills.filter((tool) => tool.id !== id);

      const newAssistantTool =
        updatedTools?.length === 0
          ? assistantValue.assistant_object.tools.filter(
              (t) => t.function?.name !== SKILLS_PLUGIN.function.name,
            )
          : assistantValue.assistant_object.tools;
      const finalInstruction = rebuildSecurityBlocks(
        assistantValue?.assistant_object?.instructions,
        meta?.tools?.assistants ?? null,
        updatedTools,
      );
      const newValue = getUpdatedValue(
        assistantValue,
        {
          instructions: finalInstruction,
          tools: newAssistantTool,
        },
        { tools: { ...meta.tools, skills: updatedTools } },
      );

      setOpenInstructionBlock(null);
      saveAssistant(newValue);
    },
    [meta?.tools],
  );

  return {
    meta,
    openInstructionBlock,
    handleDeleteSkill,
    onSelectAssistant,
    onSelectSkill,
    handleChangeInstruction,
    handleRemoveAssistant,
    handleCloseInstructionBlock,
    handleOpenInstructionBlock,
  };
};
